import type { LucideIcon } from 'lucide-react'
import { ChevronDown, ChevronRight, Download, Eye, FileText } from 'lucide-react'
import type { ReactNode } from 'react'
import { createElement } from 'react'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactPreviewHeaderActions } from '@/components/project/artifact-viewer/ArtifactPreviewHeaderActions'
import type { PreviewHeaderState } from '@/components/project/artifact-viewer/previewHeaderState'
import { ViewerTabs } from '@/components/project/artifact-viewer/ViewerTabs'
import { Button as UIButton } from '@/components/ui/button'
import { CardHeader, CardTitle } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import { getArtifactPreviewCategory, type PreviewCategory } from '@/types/artifacts'

const centerTabsWrapperClassName = 'flex items-center justify-center justify-self-center'

const THREE_COLUMN_GRID_CATEGORIES: PreviewCategory[] = [
	'audio',
	'video',
	'pdf',
	'image',
	'text',
	'bpmn',
]

function previewUsesThreeColumnGrid(artifact: ArtifactResponse): boolean {
	return THREE_COLUMN_GRID_CATEGORIES.includes(getArtifactPreviewCategory(artifact))
}

export interface PreviewArtifactDownloadMeta {
	labelKey: string
	Icon: LucideIcon
}

export interface PreviewCardHeaderProps {
	selectedArtifactName?: string | null
	artifact: ArtifactResponse | null
	headerState: PreviewHeaderState | null
	previewArtifactDownload: PreviewArtifactDownloadMeta | null
	onEdit: () => void
	onDownload: () => Promise<void>
	downloading: boolean
}

function renderCenterTabs(state: PreviewHeaderState, t: (key: string) => string): ReactNode {
	switch (state.kind) {
		case 'transcript':
			return (
				<div className={centerTabsWrapperClassName}>
					<ViewerTabs
						activeTab={state.activeTab}
						onTabChange={state.onTabChange}
						tabTranscriptLabel={t('pages.projectDetail.transcript.tabTranscript')}
						tabMetadataLabel={t('pages.projectDetail.transcript.tabMetadata')}
					/>
				</div>
			)
		case 'pdf':
			return (
				<div className={centerTabsWrapperClassName}>
					<ViewerTabs
						activeTab={state.activeTab}
						onTabChange={state.onTabChange}
						leftTab="preview"
						tabPreviewLabel={t('pages.projectDetail.artifactPreview.tabPreview')}
						tabMetadataLabel={t('pages.projectDetail.artifactPreview.tabMetadata')}
					/>
				</div>
			)
		case 'image':
			return (
				<div className={centerTabsWrapperClassName}>
					<ViewerTabs
						activeTab={state.activeTab}
						onTabChange={state.onTabChange}
						leftTab="preview"
						tabPreviewLabel={t('pages.projectDetail.artifactPreview.tabPreview')}
						tabMetadataLabel={t('pages.projectDetail.artifactPreview.tabMetadata')}
					/>
				</div>
			)
		case 'text':
		case 'bpmn':
			return (
				<div className={centerTabsWrapperClassName}>
					<ViewerTabs
						activeTab={state.activeTab}
						onTabChange={state.onTabChange}
						leftTab="preview"
						tabPreviewLabel={t('pages.projectDetail.artifactPreview.tabPreview')}
						tabMetadataLabel={t('pages.projectDetail.artifactPreview.tabMetadata')}
					/>
				</div>
			)
		case 'unsupported':
			return null
	}
}

export function PreviewCardHeader({
	selectedArtifactName,
	artifact,
	headerState,
	previewArtifactDownload,
	onEdit,
	onDownload,
	downloading,
}: PreviewCardHeaderProps) {
	const { t } = useTranslation()

	const useThreeColumnGrid = artifact !== null && previewUsesThreeColumnGrid(artifact)

	return (
		<CardHeader
			className={cn(
				'items-center gap-2 border-b border-gray-200 !pb-2 pt-2 shrink-0',
				useThreeColumnGrid ? 'grid grid-cols-[1fr_auto_1fr]' : 'flex flex-row flex-wrap justify-between'
			)}
		>
			<CardTitle className="flex items-center gap-2 min-w-0 self-center">
				<Eye className="w-5 h-5 shrink-0" />
				{t('pages.projectDetail.preview.title')}
				{selectedArtifactName ? (
					<>
						<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
						<span className="truncate">{selectedArtifactName}</span>
					</>
				) : null}
			</CardTitle>

			{headerState ? renderCenterTabs(headerState, t) : null}

			{artifact && headerState ? (
				<div className="flex justify-end items-center gap-2 shrink-0 self-center">
					{headerState.kind === 'transcript' && headerState.canExportPdf && headerState.onExportPdf && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<UIButton
									type="button"
									variant="outline"
									size="sm"
									className="shrink-0"
									aria-label={
										previewArtifactDownload
											? t('pages.projectDetail.transcript.exportMenuAriaLabel', {
													downloadItem: t(previewArtifactDownload.labelKey),
												})
											: t('pages.projectDetail.transcript.exportMenuTrigger')
									}
									aria-busy={Boolean(headerState.exportingPdf)}
								>
									{headerState.exportingPdf ? (
										<Spinner className="mr-1 h-4 w-4" aria-hidden />
									) : (
										<Download className="mr-1 h-4 w-4" aria-hidden />
									)}
									{t('pages.projectDetail.transcript.exportMenuTrigger')}
									<ChevronDown className="ml-1 h-4 w-4" aria-hidden />
								</UIButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem
									disabled={Boolean(downloading) || artifact.status === 'pending_upload'}
									onClick={() => void onDownload()}
								>
									{previewArtifactDownload ? (
										<>
											{createElement(previewArtifactDownload.Icon, {
												'aria-hidden': true,
											})}
											{t(previewArtifactDownload.labelKey)}
										</>
									) : null}
								</DropdownMenuItem>
								<DropdownMenuItem
									disabled={Boolean(headerState.exportingPdf)}
									aria-busy={Boolean(headerState.exportingPdf)}
									onClick={() => void headerState.onExportPdf?.()}
								>
									<FileText aria-hidden />
									{t('pages.projectDetail.transcript.exportAsPdf')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					<ArtifactPreviewHeaderActions
						artifact={artifact}
						onEdit={onEdit}
						onDownload={onDownload}
						downloading={downloading}
						downloadArtifactLabel={
							previewArtifactDownload ? t(previewArtifactDownload.labelKey) : undefined
						}
						hideDownload={Boolean(
							headerState.kind === 'transcript' && headerState.canExportPdf && headerState.onExportPdf
						)}
					/>
				</div>
			) : null}
		</CardHeader>
	)
}
